import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiTimeout, useBackendApi } from '@/hooks/fetch';
import type {
    FcAdminApiFailure,
    FcAdminApiResponse,
    FcAdminListData,
    FcAdminMutationOperation,
    FcAdminMutationRequest,
    FcAdminQueryRequest,
    FcAdminReadOperation,
    FcAdminStatusData,
} from '@shared/fcAdminApiTypes';
import { useAuth } from '@/hooks/auth';
import { nativeActionAttemptStore } from '@/lib/nativeActionIdempotency';
import { fcAdminReadRetryDelay, fcAdminTransportFailure, formatFcAdminError, formatFcAdminNotice } from '@/lib/fcAdminErrors';
import { useFcAdminAccessContext } from './FcAdminStatusProvider';
export { formatFcAdminError, formatFcAdminFailure, formatFcAdminNotice, isRetryableFcAdminReadFailure } from '@/lib/fcAdminErrors';
export { useFcAdminStatusContext as useFcAdminStatus } from './FcAdminStatusProvider';
export { useFcAdminAccessContext as useFcAdminAccess } from './FcAdminStatusProvider';
export { useFcAdminSummaryContext as useFcAdminSummary } from './FcAdminStatusProvider';

const ambiguousActionErrors = new Set([
    'bridge_timeout',
    'bridge_shutdown',
    'bridge_send_failed',
    'broker_timeout',
    'request_timeout',
    'idempotency_in_progress',
    'idempotency_uncertain',
    'internal_error',
    'audit_outcome_uncertain',
    'legacy_intent_uncertain',
    'legacy_projection_failed',
    'cleanup_pending',
]);

export const isAmbiguousFcAdminActionError = (code: string) => ambiguousActionErrors.has(code);

const localAccessFailure = (code: string, retryable: boolean): FcAdminApiFailure => {
    const message = formatFcAdminError(code, 'FC Admin hat den Zugriff sicherheitshalber gesperrt.');
    return {
        ok: false,
        error: message,
        code,
        message,
        retryable,
        correlationId: 'local-access-guard',
    };
};

const authorizationFailure = (status?: FcAdminStatusData) => {
    const state = status?.authorizationState;
    return localAccessFailure(state && state !== 'authorized' && state !== 'ready' ? state : 'authorization_changed', true);
};

const mutationFailure = (status?: FcAdminStatusData) => {
    if (!status?.bridgeConfigured) return localAccessFailure('bridge_not_configured', true);
    if (!status.gameServerOnline) return localAccessFailure('game_server_offline', true);
    if (!status.resourceOnline) return localAccessFailure('service_unavailable', true);
    if (status.offlineSnapshot) return localAccessFailure('offline_snapshot', true);
    return authorizationFailure(status);
};

export const useFcAdminQuery = <TData extends FcAdminListData = FcAdminListData>(operation: Exclude<FcAdminReadOperation, 'system.status'>) => {
    const { status, authorized, can, accessRevision } = useFcAdminAccessContext();
    const operationAllowed = authorized && can(operation);
    const [state, setState] = useState<{ revision: string; data?: TData; error?: string; failure?: FcAdminApiFailure; loading: boolean }>({
        revision: 'unavailable',
        loading: false,
    });
    const queryApi = useBackendApi<FcAdminApiResponse<TData>, FcAdminQueryRequest>({
        method: 'POST',
        path: '/fc/query/:operation',
        abortOnUnmount: true,
        abortOnNewRequest: true,
    });
    const queryApiRef = useRef(queryApi);
    const sequence = useRef(0);
    const mounted = useRef(true);
    const retryWait = useRef<{ timer: number; resolve: () => void }>();
    const accessRef = useRef({ revision: accessRevision, allowed: operationAllowed });
    const statusRef = useRef(status);
    queryApiRef.current = queryApi;
    accessRef.current = { revision: accessRevision, allowed: operationAllowed };
    statusRef.current = status;

    const cancelRetryWait = useCallback(() => {
        const pending = retryWait.current;
        if (!pending) return;
        retryWait.current = undefined;
        window.clearTimeout(pending.timer);
        pending.resolve();
    }, []);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            cancelRetryWait();
        };
    }, [cancelRetryWait]);

    useEffect(() => {
        sequence.current += 1;
        cancelRetryWait();
        setState({ revision: accessRevision, loading: false });
    }, [accessRevision, cancelRetryWait, operationAllowed]);

    const run = useCallback(async (request: FcAdminQueryRequest = {}): Promise<FcAdminApiResponse<TData> | FcAdminApiFailure> => {
        if (!operationAllowed) {
            const failure = authorized ? localAccessFailure('forbidden', false) : authorizationFailure(statusRef.current);
            setState({ revision: accessRevision, error: formatFcAdminNotice(failure), failure, loading: false });
            return failure;
        }
        cancelRetryWait();
        const requestRevision = accessRevision;
        const requestSequence = ++sequence.current;
        const isCurrent = () => mounted.current
            && requestSequence === sequence.current
            && accessRef.current.revision === requestRevision
            && accessRef.current.allowed;
        const waitForRetry = (delay: number) => new Promise<void>((resolve) => {
            const timer = window.setTimeout(() => {
                if (retryWait.current?.timer === timer) retryWait.current = undefined;
                resolve();
            }, delay);
            retryWait.current = { timer, resolve };
        });
        setState((current) => current.revision === requestRevision
            ? { ...current, error: undefined, loading: true }
            : { revision: requestRevision, loading: true });
        for (let attempt = 0; ; attempt += 1) {
            let transportFailure: FcAdminApiFailure | undefined;
            let response: FcAdminApiResponse<TData> | void = undefined;
            try {
                response = await queryApiRef.current({
                    pathParams: { operation },
                    data: request,
                    timeout: ApiTimeout.FC_QUERY,
                    error: (message) => { transportFailure = fcAdminTransportFailure(message); },
                });
            } catch (error) {
                transportFailure = fcAdminTransportFailure(error instanceof Error ? error.message : undefined);
            }
            if (!isCurrent()) return localAccessFailure('authorization_changed', true);
            const result: FcAdminApiResponse<TData> | FcAdminApiFailure = response ? response : transportFailure ?? fcAdminTransportFailure();
            const delay = result.ok ? undefined : fcAdminReadRetryDelay(operation, result, attempt);
            if (delay === undefined) {
                setState(result.ok
                    ? { revision: requestRevision, data: result.data, loading: false }
                    : { revision: requestRevision, error: formatFcAdminNotice(result), failure: result, loading: false });
                return result;
            }
            await waitForRetry(delay);
            if (!isCurrent()) return localAccessFailure('authorization_changed', true);
        }
    }, [accessRevision, authorized, cancelRetryWait, operation, operationAllowed]);

    const visible = operationAllowed && state.revision === accessRevision;
    const deniedError = !operationAllowed
        ? formatFcAdminNotice(authorized ? localAccessFailure('forbidden', false) : authorizationFailure(status))
        : undefined;
    const deniedFailure = !operationAllowed
        ? authorized ? localAccessFailure('forbidden', false) : authorizationFailure(status)
        : undefined;
    return {
        data: visible ? state.data : undefined,
        error: visible ? state.error : deniedError,
        failure: visible ? state.failure : deniedFailure,
        loading: visible ? state.loading : false,
        run,
    };
};

export const useFcAdminAction = (operation: FcAdminMutationOperation) => {
    const { authData } = useAuth();
    const { status, authorized, mutationReady, can, accessRevision } = useFcAdminAccessContext();
    const operationAllowed = mutationReady && can(operation);
    const actorId = authData ? authData.name : 'unauthenticated';
    const actionApi = useBackendApi<FcAdminApiResponse, FcAdminMutationRequest>({
        method: 'POST',
        path: '/fc/action/:operation',
    });
    const actionApiRef = useRef(actionApi);
    const accessRef = useRef({ revision: accessRevision, allowed: operationAllowed });
    const statusRef = useRef(status);
    actionApiRef.current = actionApi;
    accessRef.current = { revision: accessRevision, allowed: operationAllowed };
    statusRef.current = status;

    return useCallback(async (request: FcAdminMutationRequest) => {
        if (!operationAllowed) {
            if (!authorized) return authorizationFailure(statusRef.current);
            if (!can(operation)) return localAccessFailure('forbidden', false);
            return mutationFailure(statusRef.current);
        }
        const requestRevision = accessRevision;
        const attempt = await nativeActionAttemptStore.begin(
            actorId,
            operation,
            { payload: request.payload, target: request.target },
            request.idempotencyKey,
        );
        let requestError: string | undefined;
        const response = await actionApiRef.current({
            pathParams: { operation },
            data: { ...request, idempotencyKey: attempt.key },
            timeout: ApiTimeout.FC_ACTION,
            error: (message) => { requestError = message; },
        });
        if (response && (response.ok || !ambiguousActionErrors.has(response.code))) {
            nativeActionAttemptStore.complete(attempt);
            return response;
        }
        if (accessRef.current.revision !== requestRevision || !accessRef.current.allowed) {
            return localAccessFailure('authorization_changed', true);
        }
        if (!response && requestError) return undefined;
        return response;
    }, [accessRevision, actorId, authorized, can, operation, operationAllowed]);
};
